import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../utils/api';
import { toast } from 'sonner';
import { Ticket, Plus, Search, Filter, Clock, AlertCircle, CheckCircle2, CheckCircle, Loader2, Monitor, Radio, HardDrive, User, LayoutGrid, LayoutList, Calendar, UserCircle, X } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';

const Tickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all', 'assigned_to_me', 'unassigned'
  const [sortBy, setSortBy] = useState('newest');
  const [resolvingTicketId, setResolvingTicketId] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [bulkResolving, setBulkResolving] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    client_id: null,
    contact_id: null,
    asset_id: null,
    requester_name: '',
    requester_email: ''
  });

  // Read URL params on mount and when they change
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'assigned_to_me') {
      setAssignmentFilter('assigned_to_me');
    } else if (filter === 'unassigned') {
      setAssignmentFilter('unassigned');
    } else {
      setAssignmentFilter('all');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketsRes, clientsRes, agentsRes, usersRes, meRes] = await Promise.all([
        api.get('/tickets'),
        api.get('/clients'),
        api.get('/agents'),
        api.get('/users').catch(() => ({ data: { users: [] } })),
        api.get('/auth/me').catch(() => ({ data: null }))
      ]);
      setTickets(ticketsRes.data);
      setClients(clientsRes.data);
      setAgents(agentsRes.data);
      setUsers(usersRes.data?.users || []);
      setCurrentUser(meRes.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const clearAssignmentFilter = () => {
    setAssignmentFilter('all');
    setSearchParams({});
  };

  const fetchContacts = async (clientId) => {
    if (!clientId || clientId === 'none') {
      setContacts([]);
      return;
    }
    
    try {
      const res = await api.get(`/clients/${clientId}/contacts`);
      setContacts(res.data);
    } catch (error) {
      setContacts([]);
    }
  };

  const fetchAssets = async (clientId) => {
    if (!clientId || clientId === 'none') {
      setAssets([]);
      return;
    }
    
    try {
      const res = await api.get(`/agents?client_id=${clientId}`);
      setAssets(res.data);
    } catch (error) {
      setAssets([]);
    }
  };

  const handleClientChange = (clientId) => {
    setNewTicket({ ...newTicket, client_id: clientId === "none" ? null : clientId, contact_id: null, asset_id: null, requester_name: '', requester_email: '' });
    fetchContacts(clientId);
    fetchAssets(clientId);
  };

  const handleContactChange = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setNewTicket({
        ...newTicket,
        contact_id: contactId,
        requester_name: contact.name,
        requester_email: contact.email
      });
    } else {
      setNewTicket({ ...newTicket, contact_id: null });
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/tickets', newTicket);
      toast.success('Ticket created successfully');
      setShowCreateDialog(false);
      setContacts([]);
      setAssets([]);
      setNewTicket({
        title: '',
        description: '',
        priority: 'medium',
        category: 'general',
        client_id: null,
        contact_id: null,
        asset_id: null,
        requester_name: '',
        requester_email: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const getFilteredAndSortedTickets = () => {
    // Filter tickets
    let filtered = tickets.filter(ticket => {
      const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
      const matchesClient = clientFilter === 'all' || ticket.client_id === clientFilter;
      
      // Assignment filter
      let matchesAssignment = true;
      if (assignmentFilter === 'assigned_to_me' && currentUser) {
        matchesAssignment = ticket.assigned_to === currentUser.name;
      } else if (assignmentFilter === 'unassigned') {
        matchesAssignment = !ticket.assigned_to || ticket.assigned_to === '';
      }
      
      return matchesSearch && matchesStatus && matchesPriority && matchesClient && matchesAssignment;
    });

    // Sort tickets
    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (sortBy === 'newest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'closed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved':
      case 'closed': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Ticket className="w-4 h-4" />;
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.company_name : 'No Client';
  };

  const getDeviceName = (assetId) => {
    const asset = agents.find(a => a.id === assetId);
    return asset ? asset.hostname : null;
  };

  const getReportedBy = (ticket) => {
    // Try to get reported_by field first
    if (ticket.reported_by) {
      return ticket.reported_by;
    }
    
    // Fallback: extract from description if it's an agent-submitted ticket
    if (ticket.description && ticket.description.includes('Reported by:')) {
      const match = ticket.description.match(/Reported by: ([^\n]+)/);
      if (match) {
        return match[1].trim();
      }
    }
    
    return null;
  };

  const cleanDescription = (description) => {
    // Remove the "Submitted from:" section from agent-submitted tickets
    if (description && description.includes('---')) {
      const parts = description.split('---');
      return parts[0].trim(); // Return only the part before the separator
    }
    return description;
  };

  const handleDeviceClick = (e, assetId) => {
    e.stopPropagation();
    navigate(`/assets/${assetId}`);
  };

  const handleRemoteControlClick = async (e, assetId) => {
    e.stopPropagation();
    try {
      // Start remote control session
      const response = await api.post(`/agents/${assetId}/remote-control/start`, {
        protocol: 'vnc'
      });
      
      if (response.data.session_id) {
        // Open remote control in new window
        window.open(`/remote-control/${assetId}?session=${response.data.session_id}`, '_blank');
        toast.success('Remote control session started');
      }
    } catch (error) {
      toast.error('Failed to start remote control session');
      console.error('Remote control error:', error);
    }
  };

  const handleClientAssetsClick = (e, clientId) => {
    e.stopPropagation();
    navigate(`/assets-rmm?client_id=${clientId}`);
  };

  const handleQuickResolve = async (e, ticketId) => {
    e.stopPropagation();
    setResolvingTicketId(ticketId);
    try {
      await api.post(`/tickets/${ticketId}/resolve`, { send_email: false });
      setTickets(tickets.map(t => 
        t.id === ticketId ? { ...t, status: 'resolved' } : t
      ));
      setSelectedTickets(selectedTickets.filter(id => id !== ticketId));
      toast.success('Ticket resolved');
    } catch (error) {
      toast.error('Failed to resolve ticket');
    } finally {
      setResolvingTicketId(null);
    }
  };

  const handleSelectTicket = (ticketId, checked) => {
    if (checked) {
      setSelectedTickets([...selectedTickets, ticketId]);
    } else {
      setSelectedTickets(selectedTickets.filter(id => id !== ticketId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const selectableTickets = filteredTickets
        .filter(t => t.status === 'open' || t.status === 'in_progress')
        .map(t => t.id);
      setSelectedTickets(selectableTickets);
    } else {
      setSelectedTickets([]);
    }
  };

  const handleBulkResolve = async () => {
    if (selectedTickets.length === 0) return;
    
    setBulkResolving(true);
    try {
      await Promise.all(
        selectedTickets.map(ticketId => 
          api.post(`/tickets/${ticketId}/resolve`, { send_email: false })
        )
      );
      setTickets(tickets.map(t => 
        selectedTickets.includes(t.id) ? { ...t, status: 'resolved' } : t
      ));
      toast.success(`${selectedTickets.length} ticket(s) resolved`);
      setSelectedTickets([]);
    } catch (error) {
      toast.error('Failed to resolve some tickets');
    } finally {
      setBulkResolving(false);
    }
  };

  const handleAssignTicket = async (e, ticketId, userId) => {
    e.stopPropagation();
    try {
      const user = users.find(u => u.id === userId);
      const assignedName = user ? user.name : (userId === 'unassigned' ? null : userId);
      
      await api.put(`/tickets/${ticketId}`, { assigned_to: assignedName });
      setTickets(tickets.map(t => 
        t.id === ticketId ? { ...t, assigned_to: assignedName } : t
      ));
      toast.success(assignedName ? `Assigned to ${assignedName}` : 'Unassigned');
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusCounts = () => {
    return {
      all: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      in_progress: tickets.filter(t => t.status === 'in_progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      closed: tickets.filter(t => t.status === 'closed').length
    };
  };

  const filteredTickets = getFilteredAndSortedTickets();
  const statusCounts = getStatusCounts();
  const selectableTickets = filteredTickets.filter(t => t.status === 'open' || t.status === 'in_progress');
  const allSelectableSelected = selectableTickets.length > 0 && selectableTickets.every(t => selectedTickets.includes(t.id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 p-4">
      <div className="max-w-full mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Helpdesk Tickets</h1>
            <p className="text-gray-600 mt-1">Manage support tickets and customer requests</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'card' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none ${viewMode === 'card' ? 'bg-purple-600' : ''}`}
                onClick={() => setViewMode('card')}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none ${viewMode === 'table' ? 'bg-purple-600' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <LayoutList className="w-4 h-4" />
              </Button>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2 bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4" />
              New Ticket
            </Button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedTickets.length > 0 && (
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-purple-800">
                  {selectedTickets.length} ticket(s) selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedTickets([])}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    size="sm"
                    className="gap-2 bg-green-600 hover:bg-green-700"
                    onClick={handleBulkResolve}
                    disabled={bulkResolving}
                  >
                    {bulkResolving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Resolve Selected
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Filter Indicator */}
        {assignmentFilter !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Active filter:</span>
            <Badge 
              variant="secondary" 
              className="gap-1 pl-3 pr-1 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
              onClick={clearAssignmentFilter}
            >
              {assignmentFilter === 'assigned_to_me' ? 'Assigned to Me' : 'Unassigned'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-xs md:text-sm text-gray-600">Total</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900">{statusCounts.all}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('open')}>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-xs md:text-sm text-blue-600">Open</p>
                <p className="text-xl md:text-3xl font-bold text-blue-700">{statusCounts.open}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('in_progress')}>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-xs md:text-sm text-purple-600">In Progress</p>
                <p className="text-xl md:text-3xl font-bold text-purple-700">{statusCounts.in_progress}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('resolved')}>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-xs md:text-sm text-green-600">Resolved</p>
                <p className="text-xl md:text-3xl font-bold text-green-700">{statusCounts.resolved}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('closed')}>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-xs md:text-sm text-gray-600">Closed</p>
                <p className="text-xl md:text-3xl font-bold text-gray-700">{statusCounts.closed}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Search and Sort Row */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filter Row */}
              <div className="flex flex-col md:flex-row gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Ticket className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {tickets.length === 0 ? 'No tickets yet' : 'No tickets match your filters'}
              </h3>
              <p className="text-gray-500 mb-6">
                {tickets.length === 0 ? 'Create your first support ticket' : 'Try adjusting your search or filters'}
              </p>
              {tickets.length === 0 && (
                <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Ticket
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          /* Table View */
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allSelectableSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead className="w-24">Priority</TableHead>
                    <TableHead className="w-32">Updated</TableHead>
                    <TableHead className="w-32">Due</TableHead>
                    <TableHead className="w-36">Assigned To</TableHead>
                    <TableHead className="w-36">Contact</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => {
                    const isSelectable = ticket.status === 'open' || ticket.status === 'in_progress';
                    return (
                      <TableRow 
                        key={ticket.id} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/tickets/${ticket.id}`)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedTickets.includes(ticket.id)}
                            onCheckedChange={(checked) => handleSelectTicket(ticket.id, checked)}
                            disabled={!isSelectable}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="font-medium text-sm truncate">{ticket.title}</p>
                            <p className="text-xs text-gray-500">#{ticket.id.slice(0, 8)} • {getClientName(ticket.client_id) || 'No client'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(ticket.status)} text-xs`}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getPriorityColor(ticket.priority)} text-xs`}>
                            {ticket.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {formatTimeAgo(ticket.updated_at)}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {ticket.due_date ? (
                            <span className={new Date(ticket.due_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                              {new Date(ticket.due_date).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={ticket.assigned_to || 'unassigned'}
                            onValueChange={(value) => handleAssignTicket({ stopPropagation: () => {} }, ticket.id, value)}
                          >
                            <SelectTrigger className="h-8 text-xs w-full">
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {users.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {ticket.requester_name ? (
                              <div>
                                <p className="font-medium truncate max-w-[120px]">{ticket.requester_name}</p>
                                {ticket.requester_email && (
                                  <p className="text-gray-500 truncate max-w-[120px]">{ticket.requester_email}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {isSelectable && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-green-50"
                                onClick={(e) => handleQuickResolve(e, ticket.id)}
                                disabled={resolvingTicketId === ticket.id}
                                title="Quick Resolve"
                              >
                                {resolvingTicketId === ticket.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                            )}
                            {ticket.asset_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-blue-50"
                                onClick={(e) => handleDeviceClick(e, ticket.asset_id)}
                                title="View Device"
                              >
                                <Monitor className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          /* Card View */
          <div className="space-y-4">
            {filteredTickets.map((ticket) => {
              const isSelectable = ticket.status === 'open' || ticket.status === 'in_progress';
              return (
                <Card
                  key={ticket.id}
                  className={`cursor-pointer hover:shadow-lg transition-shadow ${selectedTickets.includes(ticket.id) ? 'ring-2 ring-purple-400' : ''}`}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <CardContent className="p-4 sm:p-6">
                    {/* Top Row: Checkbox, Title, Badges, Assigned, Contact, Actions */}
                    <div className="flex items-start gap-3 mb-3">
                      {/* Checkbox */}
                      <div className="flex items-center pt-1" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedTickets.includes(ticket.id)}
                          onCheckedChange={(checked) => handleSelectTicket(ticket.id, checked)}
                          disabled={!isSelectable}
                        />
                      </div>
                      
                      {/* Title and badges */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{ticket.title}</h3>
                          <Badge className={`${getStatusColor(ticket.status)} flex-shrink-0`}>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(ticket.status)}
                              <span className="text-xs">{ticket.status.replace('_', ' ')}</span>
                            </div>
                          </Badge>
                          <Badge className={`${getPriorityColor(ticket.priority)} flex-shrink-0`}>
                            <span className="text-xs">{ticket.priority}</span>
                          </Badge>
                          
                          {/* Assigned To - inline */}
                          <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={ticket.assigned_to || 'unassigned'}
                              onValueChange={(value) => handleAssignTicket({ stopPropagation: () => {} }, ticket.id, value)}
                            >
                              <SelectTrigger className="h-6 text-xs w-28 border-dashed bg-gray-50">
                                <UserCircle className="w-3 h-3 mr-1 text-gray-400" />
                                <SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                {users.map((user) => (
                                  <SelectItem key={user.id} value={user.id}>
                                    {user.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Contact/Requester - inline */}
                          {ticket.requester_name && (
                            <span className="text-xs text-purple-600 flex items-center gap-1 bg-purple-50 px-2 py-0.5 rounded">
                              <User className="w-3 h-3" />
                              {ticket.requester_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Quick Action Icons - horizontal row */}
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isSelectable && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-green-50 hover:border-green-400"
                            onClick={(e) => handleQuickResolve(e, ticket.id)}
                            disabled={resolvingTicketId === ticket.id}
                            title="Quick Resolve"
                          >
                            {resolvingTicketId === ticket.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                        )}
                        
                        {ticket.asset_id && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-400"
                              onClick={(e) => handleDeviceClick(e, ticket.asset_id)}
                              title="View Device Details"
                            >
                              <Monitor className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-purple-50 hover:border-purple-400"
                              onClick={(e) => handleRemoteControlClick(e, ticket.asset_id)}
                              title="Remote Control"
                            >
                              <Radio className="h-4 w-4 text-purple-600" />
                            </Button>
                          </>
                        )}
                        
                        {!ticket.asset_id && ticket.client_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-slate-50 hover:border-slate-400"
                            onClick={(e) => handleClientAssetsClick(e, ticket.client_id)}
                            title="View Client Assets"
                          >
                            <HardDrive className="h-4 w-4 text-slate-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2 break-words ml-8">
                      {(() => {
                        const cleanedDesc = cleanDescription(ticket.description);
                        return cleanedDesc.substring(0, 150) + (cleanedDesc.length > 150 ? '...' : '');
                      })()}
                    </p>
                    
                    {/* Bottom Info Row */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 ml-8">
                      <span className="break-all">#{ticket.id.slice(0, 8)}</span>
                      <span className="hidden sm:inline">•</span>
                      <span className="whitespace-nowrap flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(ticket.updated_at)}
                      </span>
                      {ticket.client_id && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span className="text-blue-600 truncate max-w-xs">{getClientName(ticket.client_id)}</span>
                        </>
                      )}
                      {ticket.due_date && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span className={`flex items-center gap-1 ${new Date(ticket.due_date) < new Date() ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            Due: {new Date(ticket.due_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Ticket</DialogTitle>
            <DialogDescription>
              Create a new support ticket to track and manage customer requests
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Brief description of the issue"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description * (Supports Markdown)</Label>
              <div data-color-mode="light">
                <MDEditor
                  value={newTicket.description}
                  onChange={(value) => setNewTicket({ ...newTicket, description: value || '' })}
                  preview="edit"
                  height={200}
                  textareaProps={{
                    placeholder: 'Detailed description of the issue (supports **bold**, *italic*, lists, etc.)'
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={newTicket.category}
                  onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client">Client (Optional)</Label>
              <Select
                value={newTicket.client_id || "none"}
                onValueChange={handleClientChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Client</SelectItem>
                  {clients.length === 0 ? (
                    <SelectItem value="create" disabled>
                      No clients available - Create one first
                    </SelectItem>
                  ) : (
                    clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {clients.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Create clients from the Dashboard page to link tickets to them
                </p>
              )}
            </div>

            {newTicket.client_id && contacts.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="contact">Contact (Optional)</Label>
                <Select
                  value={newTicket.contact_id || "none"}
                  onValueChange={handleContactChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Contact</SelectItem>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} - {contact.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecting a contact will auto-fill requester details below
                </p>
              </div>
            )}

            {newTicket.client_id && assets.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="asset">Related Asset (Optional)</Label>
                <Select
                  value={newTicket.asset_id || "none"}
                  onValueChange={(value) => setNewTicket({ ...newTicket, asset_id: value === "none" ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an asset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Asset</SelectItem>
                    {assets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.id}>
                        {asset.hostname} ({asset.ip_address || 'No IP'}) - {asset.status === 'online' ? '🟢 Online' : '🔴 Offline'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Link this ticket to a specific computer/device
                </p>
              </div>
            )}

            {newTicket.client_id && assets.length === 0 && contacts.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  💡 No contacts or assets found for this client. Add contacts in the client details page, or assign agents to this client in the Assets RMM page.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requester_name">Requester Name</Label>
                <Input
                  id="requester_name"
                  placeholder="John Doe"
                  value={newTicket.requester_name}
                  onChange={(e) => setNewTicket({ ...newTicket, requester_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requester_email">Requester Email</Label>
                <Input
                  id="requester_email"
                  type="email"
                  placeholder="john@example.com"
                  value={newTicket.requester_email}
                  onChange={(e) => setNewTicket({ ...newTicket, requester_email: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTicket}>Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tickets;
